import React from "react";

import type { UserID } from "../../../../../hooks/useAccount";
import { useProject } from "../../../../../hooks/useProject";
import {
  type ProjectAccessRequest,
  type ProjectAccessRequestID,
  ProjectUserAssignableRoles,
  ProjectUserRoleDisplayName,
  useAcceptProjectAccessRequest,
} from "../../../../../hooks/useProjectSharing";
import { useTeamActiveSubscription } from "../../../../../hooks/useTeamActiveSubscription";
import { ShareRow, type ShareRowRef } from "./ShareRow";

interface RequestAccessRowProps {
  request: ProjectAccessRequest;
  onRemove?: (userId: ProjectAccessRequestID) => void;
  containerIntersectionObserver?: IntersectionObserver | null;
  onAccepted?: (userId: UserID, role: string) => void;
  showPaywall?: () => void;
}

export const RequestAccessRowProject = React.forwardRef<ShareRowRef, RequestAccessRowProps>(
  ({ request, onRemove, containerIntersectionObserver, onAccepted, showPaywall }, ref) => {
    const {
      mutateAsync: mutateAcceptProjectAccessRequest,
      isPending: isAcceptPending,
      isError: isAcceptError,
    } = useAcceptProjectAccessRequest({});

    const { data: projectData } = useProject();
    const { data: isTeamSubscribed, isPending: isSubscriptionDataPending } =
      useTeamActiveSubscription(projectData?.teamId);
    return (
      <ShareRow
        ref={ref}
        roles={ProjectUserAssignableRoles}
        proRoles={new Set<(typeof ProjectUserAssignableRoles)[number]>(["editor"])}
        showPaywall={showPaywall}
        isProRoleEligible={isTeamSubscribed ?? false}
        roleDisplayName={ProjectUserRoleDisplayName}
        entity={request}
        containerIntersectionObserver={containerIntersectionObserver}
        onUpdate={async (_, newRole) => {
          await mutateAcceptProjectAccessRequest({
            projectAccessRequestId: request.accessRequestId,
            role: newRole,
          });
          onAccepted?.(request.userId, newRole);
        }}
        onRemove={() => {
          onRemove?.(request.accessRequestId);
        }}
        isPending={isAcceptPending || isSubscriptionDataPending}
        isError={isAcceptError}
        isRequestAccess
        removeActionText={"Deny Access"}
      />
    );
  }
);
